import { Button, Pill, Tooltip } from '@innovationdepartment/proxima-ui';
import {
  AutoAwesomeOutlined,
  ChevronLeftRounded,
  ChevronRightRounded,
  ViewListOutlined
} from '@mui/icons-material';
import { type ReactNode, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import SectionLayout from 'ui/SectionLayout';
import BackToAudiencesLink from '../../components/BackToAudiencesLink';
import ColumnMapper from '../../components/ColumnMapper';
import FilterRules from '../../components/FilterRules/FilterRules';
import { SqlPanel, SqlToggleButton, useSqlPreview } from '../../components/FilterRules/SqlPreview';
import FilterSummary from '../../components/FilterSummary/FilterSummary';
import {
  Center,
  CollapseButton,
  CollapsedRail,
  CountNote,
  CountPanel,
  DefinitionFieldLabel,
  DefinitionHeading,
  DefinitionPanel,
  EmptyRules,
  HeaderActions,
  HeaderRow,
  Layout,
  Notice,
  PanelSection,
  PanelSectionHeading,
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarTitle,
  StripRow,
  TextInput,
  TwoColumns
} from '../../components/form.styles';
import NlGenerator from '../../components/NlGenerator';
import { PrimaryButton } from '../../components/shared.styles';
import type { AudienceFieldName } from '../../lib/audienceFields';
import { audienceField } from '../../lib/audienceFields';
import type { FilterErrors } from '../../lib/filterValidation';
import { droppedReasonsByRule, PROPOSED_REASON } from '../../lib/queryMapping';
import type { TosCheck } from '../../store/destinationsSlice';
import type { TargetSelection, WizardState } from '../../store/wizardTypes';
import type {
  AdAccountsResponse,
  AdPlatform,
  AudienceFilters,
  LookalikeRatio,
  ValueMetric
} from '../../types';
import BrandShareField from './BrandShareField';
import CountBar from './CountBar';
import DeliverStep from './DeliverStep';

const DISCARD_DRAFT_MESSAGE = 'Discard this custom audience? The preview and your rules are lost.';

export type CreateDefinitionViewProps = {
  wizard: WizardState;
  viewerPath: string;
  /** Name of the definition this draft was cloned from, if any. */
  cloneOf: string | null;
  checkedFields: Set<string>;
  promptStale: boolean;
  countStale: boolean;
  filterErrors: FilterErrors;
  countBlocker: string | null;
  createBlocker: string | null;
  /** Why Create is disabled on the Deliver step, or null when it is enabled. */
  deliverBlocker: string | null;
  /** Whether the unconfirmed-create notice's Create it anyway is disabled. */
  createAnywayDisabled: boolean;
  /** Whether leaving by the back arrow asks before it discards the draft. */
  confirmDiscard: boolean;
  targets: TargetSelection;
  adAccounts: AdAccountsResponse | null;
  adAccountsError: string | null;
  tosCheckByAdAccountId: Record<string, TosCheck>;
  onRecheckTos: (adAccountId: string) => void;
  onPromptChange: (prompt: string) => void;
  onSubmitPrompt: () => void;
  onAcceptPreview: () => void;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onBrandShareChange: (brandSharePercent: number | null) => void;
  onQueryChange: (query: RuleGroupType) => void;
  onToggleField: (name: AudienceFieldName) => void;
  onClearFilter: (patch: Partial<AudienceFilters>) => void;
  onCount: () => void;
  onNext: () => void;
  onBack: () => void;
  onSelectPlatform: (platform: AdPlatform) => void;
  onToggleAdAccount: (adAccountId: string) => void;
  onToggleRatio: (ratio: LookalikeRatio) => void;
  onSelectValueMetric: (valueMetric: ValueMetric | null) => void;
  onCreate: () => void;
  onCreateAnyway: () => void;
};

const CreateDefinitionView = (props: CreateDefinitionViewProps) => {
  const { wizard } = props;
  const [generatorOpen, setGeneratorOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const hasRules = wizard.query.rules.length > 0;
  const hasDraft = hasRules || wizard.name.trim() !== '' || wizard.description.trim() !== '';
  const sqlPreview = useSqlPreview(wizard.query);
  const notSent = wizard.dropped
    .filter(item => item.reason === PROPOSED_REASON)
    .map(item => audienceField(item.field)?.label ?? item.field);

  const stepBlocker = wizard.step === 'define' ? props.createBlocker : props.deliverBlocker;

  return (
    <SectionLayout
      title={props.cloneOf ? `New custom audience from ${props.cloneOf}` : 'New custom audience'}
      aboveTitle={
        <BackToAudiencesLink
          to={props.viewerPath}
          confirmDiscard={props.confirmDiscard ? DISCARD_DRAFT_MESSAGE : null}
          disabled={wizard.submitting}
        />
      }
      headerContent={
        <HeaderRow>
          <HeaderActions>
            {stepBlocker && !wizard.submitting ? (
              <Pill variant="warning" label={stepBlocker} />
            ) : null}
            {wizard.step === 'define' ? (
              <PrimaryButton
                label="Next: Deliver"
                size="small"
                disabled={props.createBlocker != null}
                onClick={props.onNext}
              />
            ) : (
              <>
                <PrimaryButton
                  label={wizard.submitting ? 'Creating…' : 'Create audience'}
                  size="small"
                  disabled={props.deliverBlocker != null || wizard.submitting}
                  onClick={props.onCreate}
                />
                <Button
                  label="Back"
                  variant="outlined"
                  size="small"
                  disabled={wizard.submitting}
                  onClick={props.onBack}
                />
              </>
            )}
          </HeaderActions>
        </HeaderRow>
      }
    >
      {wizard.step === 'deliver' ? (
        <DeliverStep
          targets={props.targets}
          adAccounts={props.adAccounts}
          adAccountsError={props.adAccountsError}
          tosCheckByAdAccountId={props.tosCheckByAdAccountId}
          onRecheckTos={props.onRecheckTos}
          errors={wizard.createErrors}
          unconfirmedCreate={wizard.unconfirmedCreate}
          viewerPath={props.viewerPath}
          onCreateAnyway={props.onCreateAnyway}
          createAnywayDisabled={props.createAnywayDisabled || wizard.submitting}
          onSelectPlatform={props.onSelectPlatform}
          onToggleAdAccount={props.onToggleAdAccount}
          onToggleRatio={props.onToggleRatio}
          onSelectValueMetric={props.onSelectValueMetric}
        />
      ) : (
        <Layout leftOpen={generatorOpen} rightOpen={filtersOpen}>
          <CollapsibleSidebar
            title="Audience generator"
            icon={<AutoAwesomeOutlined />}
            side="left"
            open={generatorOpen}
            onToggle={() => setGeneratorOpen(previous => !previous)}
          >
            <NlGenerator
              prompt={wizard.prompt}
              preview={wizard.preview}
              acceptedPreview={wizard.acceptedPreview}
              hasDraft={hasDraft}
              promptStale={props.promptStale}
              onPromptChange={props.onPromptChange}
              onSubmit={props.onSubmitPrompt}
              onAccept={props.onAcceptPreview}
            />
          </CollapsibleSidebar>

          <Center>
            <DefinitionPanel aria-labelledby="audience-definition-heading">
              <DefinitionHeading id="audience-definition-heading">
                Audience Definition
              </DefinitionHeading>
              <TwoColumns>
                <DefinitionFieldLabel>
                  Name
                  <TextInput
                    value={wizard.name}
                    placeholder="e.g. Warm States Homeowners"
                    onChange={event => props.onNameChange(event.target.value)}
                  />
                </DefinitionFieldLabel>
                <DefinitionFieldLabel>
                  Description
                  <TextInput
                    value={wizard.description}
                    placeholder="e.g. Homeowners in warm-climate states who deal with heat and dry weather"
                    onChange={event => props.onDescriptionChange(event.target.value)}
                  />
                </DefinitionFieldLabel>
              </TwoColumns>

              <PanelSection aria-labelledby="filters-heading">
                <PanelSectionHeading id="filters-heading">Filters</PanelSectionHeading>
                <StripRow role="group" aria-label="Active filters">
                  <span>Active:</span>
                  <div>
                    <FilterSummary
                      filters={wizard.filters}
                      layout="strip"
                      onClear={props.onClearFilter}
                    />
                  </div>
                </StripRow>
                {hasRules ? null : (
                  <EmptyRules>
                    Check columns in Add Filters on the right to add rules, or describe the audience
                    in the Audience generator on the left and use the preview.
                  </EmptyRules>
                )}
                <FilterRules
                  query={wizard.query}
                  onQueryChange={props.onQueryChange}
                  filterErrors={props.filterErrors}
                  droppedReasons={droppedReasonsByRule(wizard.dropped)}
                />
              </PanelSection>

              <PanelSection aria-labelledby="seed-mix-heading">
                <PanelSectionHeading id="seed-mix-heading">Seed mix</PanelSectionHeading>
                <CountNote tone="muted">
                  The brand's top customers by spend fill the brand share of the seed. Prospects
                  that best match the rules fill the rest.
                </CountNote>
                <BrandShareField
                  value={wizard.brandSharePercent}
                  uniqueIndividualLimit={wizard.filters.uniqueIndividualLimit}
                  onChange={props.onBrandShareChange}
                />
              </PanelSection>

              <PanelSection aria-labelledby="preview-heading">
                <PanelSectionHeading id="preview-heading">Preview</PanelSectionHeading>
                <CountPanel>
                  <CountBar
                    count={wizard.count}
                    stale={props.countStale}
                    canCount={props.countBlocker == null}
                    blockedReason={props.countBlocker}
                    onCount={props.onCount}
                    secondaryAction={<SqlToggleButton preview={sqlPreview} />}
                    notSent={notSent}
                  />
                </CountPanel>
                <SqlPanel preview={sqlPreview} />
              </PanelSection>
            </DefinitionPanel>

            {wizard.createErrors.length > 0 ? (
              <Notice tone="error" role="alert">
                <ul>
                  {wizard.createErrors.map(error => (
                    <li key={`${error.path}-${error.message}`}>
                      <code>{error.path}</code>: {error.message}
                    </li>
                  ))}
                </ul>
              </Notice>
            ) : null}
          </Center>

          <CollapsibleSidebar
            title="Add Filters"
            info="These filters define the audience that will be delivered."
            icon={<ViewListOutlined />}
            side="right"
            open={filtersOpen}
            onToggle={() => setFiltersOpen(previous => !previous)}
          >
            <ColumnMapper
              checkedFields={props.checkedFields}
              lookbackDays={wizard.filters.purchaseLookbackDays}
              onToggle={props.onToggleField}
            />
          </CollapsibleSidebar>
        </Layout>
      )}
    </SectionLayout>
  );
};

type CollapsibleSidebarProps = {
  title: string;
  /** Shown as an info tooltip beside the title. */
  info?: string;
  icon: ReactNode;
  side: 'left' | 'right';
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
};

const CollapsibleSidebar = ({
  title,
  info,
  icon,
  side,
  open,
  onToggle,
  children
}: CollapsibleSidebarProps) => {
  const collapseIcon = side === 'left' ? <ChevronLeftRounded /> : <ChevronRightRounded />;
  const expandIcon = side === 'left' ? <ChevronRightRounded /> : <ChevronLeftRounded />;
  if (!open)
    return (
      <CollapsedRail
        type="button"
        aria-label={`Show ${title}`}
        aria-expanded={false}
        onClick={onToggle}
      >
        {expandIcon}
        {icon}
        <span>{title}</span>
      </CollapsedRail>
    );
  return (
    <Sidebar aria-label={title}>
      <SidebarHeader>
        <SidebarTitle>
          {title}
          {info ? <Tooltip info title={info} placement="right" /> : null}
        </SidebarTitle>
        <CollapseButton type="button" aria-label={`Hide ${title}`} aria-expanded onClick={onToggle}>
          {collapseIcon}
        </CollapseButton>
      </SidebarHeader>
      <SidebarBody>{children}</SidebarBody>
    </Sidebar>
  );
};

export default CreateDefinitionView;
